export const CALENDAR_PERSPECTIVES = ['practitioners', 'resources'] as const;
export type CalendarPerspective = (typeof CALENDAR_PERSPECTIVES)[number];

interface NamedSortable {
  id: string;
  name: string;
  is_active: boolean;
  sort_order: number;
}

export const CALENDAR_PERSPECTIVE_STORAGE_KEY = 'appointments-perspective';

export interface CalendarResourceColumn {
  id: string;
  name: string;
  subtitle: string;
  resource_group_id: string;
}

export interface OccupyingAllocationRef {
  appointment_id: string;
  resource_id: string | null;
  occupies?: boolean;
}

export function parseCalendarPerspective(
  value: string | null | undefined,
): CalendarPerspective {
  return value === 'resources' ? 'resources' : 'practitioners';
}

export function buildResourceCalendarColumns(
  groups: NamedSortable[],
  resources: Array<NamedSortable & { resource_group_id: string }>,
  selectedIds: string[] | null,
): CalendarResourceColumn[] {
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const active = resources.filter((resource) => resource.is_active);
  const visible =
    selectedIds == null
      ? active
      : active.filter((resource) => selectedIds.includes(resource.id));

  return visible
    .slice()
    .sort((left, right) => {
      const leftGroup = groupById.get(left.resource_group_id);
      const rightGroup = groupById.get(right.resource_group_id);
      const groupSort = (leftGroup?.sort_order ?? 0) - (rightGroup?.sort_order ?? 0);
      if (groupSort !== 0) return groupSort;
      const groupName = (leftGroup?.name ?? '').localeCompare(rightGroup?.name ?? '');
      if (groupName !== 0) return groupName;
      if (left.sort_order !== right.sort_order) return left.sort_order - right.sort_order;
      return left.name.localeCompare(right.name);
    })
    .map((resource) => ({
      id: resource.id,
      name: resource.name,
      subtitle: groupById.get(resource.resource_group_id)?.name ?? '',
      resource_group_id: resource.resource_group_id,
    }));
}

export function occupyingResourceIdsByAppointment(
  rows: OccupyingAllocationRef[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const row of rows) {
    if (row.occupies === false || !row.resource_id) continue;
    const current = map.get(row.appointment_id) ?? [];
    if (!current.includes(row.resource_id)) current.push(row.resource_id);
    map.set(row.appointment_id, current);
  }
  return map;
}

export function appointmentsInResourceColumn<T extends { id: string }>(
  appointments: T[],
  resourceId: string | undefined,
  resourceIdsByAppointment: Map<string, string[]>,
): T[] {
  if (!resourceId) return appointments;
  if (resourceId === 'unassigned') {
    return appointments.filter((appointment) => {
      const ids = resourceIdsByAppointment.get(appointment.id);
      return !ids || ids.length === 0;
    });
  }
  return appointments.filter((appointment) =>
    (resourceIdsByAppointment.get(appointment.id) ?? []).includes(resourceId),
  );
}

export function toggleResourceSelection(
  selectedIds: string[] | null,
  resourceId: string,
  allActiveIds: string[],
): string[] | null {
  const current = new Set(selectedIds ?? allActiveIds);
  if (current.has(resourceId)) current.delete(resourceId);
  else current.add(resourceId);
  return normalizeResourceSelection(allActiveIds.filter((id) => current.has(id)), allActiveIds);
}

export function toggleResourceGroupSelection(
  selectedIds: string[] | null,
  groupResourceIds: string[],
  allActiveIds: string[],
): string[] | null {
  const current = new Set(selectedIds ?? allActiveIds);
  const allSelected = groupResourceIds.every((id) => current.has(id));
  for (const id of groupResourceIds) {
    if (allSelected) current.delete(id);
    else current.add(id);
  }
  return normalizeResourceSelection(allActiveIds.filter((id) => current.has(id)), allActiveIds);
}

export function normalizeResourceSelection(
  selectedIds: string[],
  allActiveIds: string[],
): string[] | null {
  if (allActiveIds.length > 0 && selectedIds.length === allActiveIds.length) {
    const selected = new Set(selectedIds);
    if (allActiveIds.every((id) => selected.has(id))) return null;
  }
  return selectedIds;
}

export function resourceFilterMatches(
  name: string,
  groupName: string,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return name.toLowerCase().includes(needle) || groupName.toLowerCase().includes(needle);
}
